import { api } from './client.js'

/**
 * Every API call the app makes, in one place.
 *
 * Components never build a URL themselves. That keeps route changes to a
 * single file and makes it obvious, reading this, exactly what surface the
 * client depends on.
 */

export const authApi = {
  register: (input) => api.post('/auth/register', input),
  login: (input) => api.post('/auth/login', input),
  /** Attaches an email to an account created without one, or replaces it. */
  setEmail: (email) => api.post('/auth/email', { email }),
  logout: () => api.post('/auth/logout'),
  logoutAll: () => api.post('/auth/logout-all'),
  refresh: () => api.refresh(),
  me: () => api.get('/auth/me'),
  sessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  /** WebAuthn. Sign-in verify returns the same session shape as `login`. */
  passkeys: {
    list: () => api.get('/auth/passkeys'),
    registerOptions: () => api.post('/auth/passkeys/register/options'),
    registerVerify: (input) => api.post('/auth/passkeys/register/verify', input),
    remove: (id) => api.delete(`/auth/passkeys/${id}`),
    loginOptions: (input) => api.post('/auth/passkeys/login/options', input),
    loginVerify: (input) => api.post('/auth/passkeys/login/verify', input),
  },
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
}

export const settingsApi = {
  public: () => api.get('/settings'),
}

export const catalogApi = {
  platforms: () => api.get('/catalog/platforms'),
  platform: (slug) => api.get(`/catalog/platforms/${slug}`),
  categories: (query) => api.get('/catalog/categories', query),
  services: (query) => api.get('/catalog/services', query),
  service: (id) => api.get(`/catalog/services/${id}`),
  /** The whole tree, for the order form's cascading selects. */
  tree: () => api.get('/catalog/catalog'),
  featured: () => api.get('/catalog/featured'),
}

export const orderApi = {
  quote: (input) => api.post('/orders/quote', input),
  linkPreview: (input) => api.post('/orders/link-preview', input),
  create: (input, idempotencyKey) =>
    api.post('/orders', input, idempotencyKey ? { headers: { 'X-Idempotency-Key': idempotencyKey } } : {}),
  /** Several orders on one link, placed together. */
  batch: (input, idempotencyKey) =>
    api.post(
      '/orders/batch',
      input,
      idempotencyKey ? { headers: { 'X-Idempotency-Key': idempotencyKey } } : {},
    ),
  list: (query) => api.get('/orders', query),
  stats: () => api.get('/orders/stats'),
  get: (id) => api.get(`/orders/${id}`),
  refill: (id) => api.post(`/orders/${id}/refill`),
  cancel: (id) => api.post(`/orders/${id}/cancel`),
  reorder: (id) => api.post(`/orders/${id}/reorder`),
}

/** Ordering without an account. Nothing here is signed in. */
export const guestApi = {
  methods: () => api.get('/guest/methods'),
  linkPreview: (input) => api.post('/guest/link-preview', input),
  quote: (input) => api.post('/guest/quote', input),
  checkout: (input) => api.post('/guest/checkout', input),
  paymentStatus: (reference) => api.get(`/guest/payments/${reference}`),
  claim: (token) => api.get(`/guest/claim/${token}`),
  claimAccount: (token, input) => api.post(`/guest/claim/${token}`, input),
}

export const walletApi = {
  get: () => api.get('/wallet'),
  transactions: (query) => api.get('/wallet/transactions', query),
  transaction: (id) => api.get(`/wallet/transactions/${id}`),
  summary: (days) => api.get('/wallet/summary', { days }),
}

export const paymentApi = {
  methods: () => api.get('/payments/methods'),
  deposit: (input) => api.post('/payments/deposit', input),
  list: (query) => api.get('/payments', query),
  get: (id) => api.get(`/payments/${id}`),
  byReference: (reference) => api.get(`/payments/reference/${reference}`),
  /** After a hosted checkout: where this invoice's customer should land. */
  returnFor: (invoiceId) => api.get(`/guest/return/${invoiceId}`),
}

export const supportApi = {
  list: (query) => api.get('/support', query),
  create: (input) => api.post('/support', input),
  get: (id) => api.get(`/support/${id}`),
  reply: (id, body) => api.post(`/support/${id}/messages`, { body }),
  close: (id) => api.post(`/support/${id}/close`),
}

/**
 * The public help centre: guest tickets opened by number and key, and orders
 * found by proving they are yours.
 */
export const contactApi = {
  send: (input) => api.post('/contact', input),
  get: (number, key) => api.get(`/contact/${number}`, { key }),
  reply: (number, key, body) => api.post(`/contact/${number}/messages`, { key, body }),
  findOrder: (input) => api.post('/contact/orders/find', input),
  order: (token) => api.get(`/contact/orders/${token}`),
  escalate: (token, body, contact) => api.post(`/contact/orders/${token}/escalate`, { body, contact }),
  linkEmail: (token, email) => api.post(`/contact/orders/${token}/email`, { email }),
}

export const accountApi = {
  profile: () => api.get('/account/profile'),
  updateProfile: (input) => api.patch('/account/profile', input),
  updatePreferences: (input) => api.patch('/account/preferences', input),
  deleteAccount: () => api.delete('/account'),

  notifications: (query) => api.get('/account/notifications', query),
  markRead: (id) => api.post(`/account/notifications/${id}/read`),
  markAllRead: () => api.post('/account/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/account/notifications/${id}`),

  apiKeys: () => api.get('/account/api-keys'),
  createApiKey: (name) => api.post('/account/api-keys', { name }),
  revokeApiKey: (id) => api.delete(`/account/api-keys/${id}`),
}

export const dashboardApi = {
  get: () => api.get('/dashboard'),
}

/* ────────────────────────────────────────────────────────────────── admin ── */

export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),

  users: {
    list: (query) => api.get('/admin/users', query),
    get: (id) => api.get(`/admin/users/${id}`),
    update: (id, input) => api.patch(`/admin/users/${id}`, input),
    changeRole: (id, role) => api.patch(`/admin/users/${id}/role`, { role }),
    adjustBalance: (id, input) => api.post(`/admin/users/${id}/balance`, input),
    resetPassword: (id, password) => api.post(`/admin/users/${id}/reset-password`, { password }),
    unlock: (id) => api.post(`/admin/users/${id}/unlock`),
    sessions: (id) => api.get(`/admin/users/${id}/sessions`),
    revokeSessions: (id) => api.delete(`/admin/users/${id}/sessions`),
  },

  orders: {
    list: (query) => api.get('/admin/orders', query),
    get: (id) => api.get(`/admin/orders/${id}`),
    resubmit: (id) => api.post(`/admin/orders/${id}/resubmit`),
    sync: (id) => api.post(`/admin/orders/${id}/sync`),
    forceStatus: (id, input) => api.post(`/admin/orders/${id}/status`, input),
    refund: (id, input) => api.post(`/admin/orders/${id}/refund`, input),
    cancel: (id) => api.post(`/admin/orders/${id}/cancel`),
  },

  platforms: {
    list: () => api.get('/admin/platforms'),
    create: (input) => api.post('/admin/platforms', input),
    update: (id, input) => api.patch(`/admin/platforms/${id}`, input),
    remove: (id) => api.delete(`/admin/platforms/${id}`),
  },

  categories: {
    list: (query) => api.get('/admin/categories', query),
    create: (input) => api.post('/admin/categories', input),
    update: (id, input) => api.patch(`/admin/categories/${id}`, input),
    remove: (id) => api.delete(`/admin/categories/${id}`),
  },

  services: {
    list: (query) => api.get('/admin/services', query),
    get: (id) => api.get(`/admin/services/${id}`),
    create: (input) => api.post('/admin/services', input),
    update: (id, input) => api.patch(`/admin/services/${id}`, input),
    remove: (id) => api.delete(`/admin/services/${id}`),
    previewPricing: (serviceIds, markupPercent) =>
      api.post('/admin/services/preview-pricing', { serviceIds, markupPercent }),
    bulk: (input) => api.post('/admin/services/bulk', input),
  },

  pricing: {
    tiers: () => api.get('/admin/pricing/tiers'),
    createTier: (input) => api.post('/admin/pricing/tiers', input),
    updateTier: (id, input) => api.patch(`/admin/pricing/tiers/${id}`, input),
    deleteTier: (id) => api.delete(`/admin/pricing/tiers/${id}`),
    review: () => api.get('/admin/pricing/tiers/review'),
    clearReview: (userId) => api.post(`/admin/pricing/tiers/review/${userId}/clear`),
    runReview: () => api.post('/admin/pricing/tiers/review/run'),
    assign: (input) => api.post('/admin/pricing/tiers/assign', input),

    rates: () => api.get('/admin/pricing/rates'),
    setRate: (input) => api.put('/admin/pricing/rates', input),
    deleteRate: (currency) => api.delete(`/admin/pricing/rates/${currency}`),

    tax: () => api.get('/admin/pricing/tax'),
    setTax: (input) => api.put('/admin/pricing/tax', input),
    recomputeTax: () => api.post('/admin/pricing/tax/recompute'),
  },

  providers: {
    adapters: () => api.get('/admin/providers/adapters'),
    presets: () => api.get('/admin/providers/presets'),
    scoreboard: (query) => api.get('/admin/providers/routing/scoreboard', query),
    routingPreview: (query) => api.get('/admin/providers/routing/preview', query),
    recomputeRouting: () => api.post('/admin/providers/routing/recompute'),
    list: () => api.get('/admin/providers'),
    get: (id) => api.get(`/admin/providers/${id}`),
    create: (input) => api.post('/admin/providers', input),
    update: (id, input) => api.patch(`/admin/providers/${id}`, input),
    remove: (id) => api.delete(`/admin/providers/${id}`),

    test: (id) => api.post(`/admin/providers/${id}/test`),
    balance: (id) => api.post(`/admin/providers/${id}/balance`),
    sync: (id, async_ = false) => api.post(`/admin/providers/${id}/sync`, { async: async_ }),
    syncReports: (id) => api.get(`/admin/providers/${id}/sync-reports`),

    services: (id, query) => api.get(`/admin/providers/${id}/services`, query),
    import: (id, input) => api.post(`/admin/providers/${id}/import`, input),
    disableMissing: (id) => api.post(`/admin/providers/${id}/disable-missing`),
    /** Off and on again: routing status plus the services that depend on it. */
    disablePreview: (id) => api.get(`/admin/providers/${id}/disable-preview`),
    disable: (id, input = {}) => api.post(`/admin/providers/${id}/disable`, input),
    enable: (id, input = {}) => api.post(`/admin/providers/${id}/enable`, input),
    /** Start fresh: everything this provider's imports created, gone. */
    purgePreview: (id) => api.get(`/admin/providers/${id}/purge-preview`),
    purgeImported: (id) => api.delete(`/admin/providers/${id}/imported`),

    createMapping: (input) => api.post('/admin/providers/mappings', input),
    deleteMapping: (id) => api.delete(`/admin/providers/mappings/${id}`),
  },

  tickets: {
    list: (query) => api.get('/admin/tickets', query),
    get: (id) => api.get(`/admin/tickets/${id}`),
    reply: (id, body, isInternal = false) => api.post(`/admin/tickets/${id}/messages`, { body, isInternal }),
    update: (id, input) => api.patch(`/admin/tickets/${id}`, input),
  },

  payments: {
    list: (query) => api.get('/admin/payments', query),
    settle: (id, status, reason) => api.post(`/admin/payments/${id}/settle`, { status, reason }),
  },

  finance: {
    transactions: (query) => api.get('/admin/transactions', query),
    walletSummary: () => api.get('/admin/wallet/summary'),
    reconcile: (userId) => api.get(`/admin/wallet/reconcile/${userId}`),
  },

  audit: (query) => api.get('/admin/audit', query),

  jobs: {
    list: (query) => api.get('/admin/jobs', query),
    retry: (id) => api.post(`/admin/jobs/${id}/retry`),
    remove: (id) => api.delete(`/admin/jobs/${id}`),
  },

  settings: {
    get: () => api.get('/admin/settings'),
    update: (input) => api.patch('/admin/settings', input),
  },
}
